import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const digitalProducts = [
  {
    id: 1,
    title: "Complete PMO Toolkit",
    category: "Templates & Frameworks",
    price: 197,
    originalPrice: 297,
    description: "Comprehensive project management office setup including templates, frameworks, and implementation guides.",
    features: [
      "20+ PMO templates and documents",
      "Project governance frameworks",
      "Risk management matrices",
      "Stakeholder management tools",
      "Performance tracking dashboards",
      "Implementation roadmap"
    ],
    image: "https://public.readdy.ai/ai/img_res/00aba6c1f40335f8364451021d016b11.jpg",
    downloadSize: "45 MB",
    fileTypes: ["Excel", "Word", "PowerPoint", "PDF"],
    bestseller: true
  },
  {
    id: 2,
    title: "Digital Strategy Playbook",
    category: "Guides & Strategies",
    price: 147,
    originalPrice: 197,
    description: "Step-by-step guide to digital transformation with proven strategies and implementation frameworks.",
    features: [
      "Digital transformation roadmap",
      "Technology assessment tools",
      "Change management frameworks",
      "ROI calculation templates",
      "Vendor evaluation matrices",
      "Implementation checklists"
    ],
    image: "https://readdy.ai/api/search-image?query=modern%20digital%20strategy%20guide%20cover%20with%20technology%20transformation%20graphics%2C%20professional%20business%20playbook%20design%20with%20clean%20tech-focused%20layout%20and%20strategic%20planning%20elements&width=600&height=400&seq=product2&orientation=landscape",
    downloadSize: "28 MB",
    fileTypes: ["PDF", "Excel", "PowerPoint"],
    featured: true
  },
  {
    id: 3,
    title: "ISO Process Documentation Suite",
    category: "Compliance & SOPs",
    price: 127,
    originalPrice: 167,
    description: "Complete ISO compliance documentation including SOPs, process maps, and audit preparation materials.",
    features: [
      "ISO 9001 compliant templates",
      "Process mapping tools",
      "SOP documentation templates",
      "Audit preparation checklists",
      "Quality management systems",
      "Continuous improvement frameworks"
    ],
    image: "https://readdy.ai/api/search-image?query=professional%20ISO%20compliance%20documentation%20cover%20with%20quality%20management%20system%20graphics%2C%20clean%20business%20process%20documentation%20design%20with%20certification%20and%20audit%20preparation%20elements&width=600&height=400&seq=product3&orientation=landscape",
    downloadSize: "35 MB",
    fileTypes: ["Word", "Excel", "Visio", "PDF"]
  },
  {
    id: 4,
    title: "Website Launch Checklist Pro",
    category: "Digital Marketing",
    price: 67,
    originalPrice: 97,
    description: "Comprehensive website launch checklist ensuring nothing is missed in your digital presence deployment.",
    features: [
      "Pre-launch testing protocols",
      "SEO optimization checklist",
      "Performance monitoring setup",
      "Analytics configuration guide",
      "Security implementation steps",
      "Post-launch maintenance plan"
    ],
    image: "https://readdy.ai/api/search-image?query=modern%20website%20launch%20checklist%20design%20with%20digital%20marketing%20elements%2C%20professional%20web%20development%20guide%20cover%20with%20clean%20tech%20interface%20graphics%20and%20deployment%20planning%20visuals&width=600&height=400&seq=product4&orientation=landscape",
    downloadSize: "12 MB",
    fileTypes: ["PDF", "Excel", "Word"]
  },
  {
    id: 5,
    title: "Executive Dashboard Templates",
    category: "Templates & Frameworks",
    price: 87,
    originalPrice: 127,
    description: "Professional executive dashboard templates for performance tracking and strategic decision making.",
    features: [
      "KPI tracking dashboards",
      "Executive summary templates",
      "Performance metrics frameworks",
      "Strategic planning tools",
      "Budget tracking systems",
      "Automated reporting templates"
    ],
    image: "https://readdy.ai/api/search-image?query=professional%20executive%20dashboard%20template%20design%20with%20business%20analytics%20charts%2C%20clean%20corporate%20reporting%20interface%20with%20KPI%20tracking%20elements%20and%20strategic%20planning%20graphics&width=600&height=400&seq=product5&orientation=landscape",
    downloadSize: "22 MB",
    fileTypes: ["Excel", "PowerPoint", "PDF"]
  },
  {
    id: 6,
    title: "Social Media Strategy Bundle",
    category: "Digital Marketing",
    price: 97,
    originalPrice: 147,
    description: "Complete social media strategy package with content planning, scheduling, and analytics tools.",
    features: [
      "Content planning calendars",
      "Social media audit templates",
      "Engagement strategy frameworks",
      "Analytics reporting tools",
      "Brand voice guidelines",
      "Campaign tracking systems"
    ],
    image: "https://readdy.ai/api/search-image?query=modern%20social%20media%20strategy%20bundle%20cover%20with%20digital%20marketing%20graphics%2C%20professional%20content%20planning%20templates%20and%20social%20media%20management%20tools%20in%20clean%20business%20design&width=600&height=400&seq=product6&orientation=landscape",
    downloadSize: "18 MB",
    fileTypes: ["Excel", "PowerPoint", "PDF", "Canva"]
  }
];

const categories = ["All Products", "Templates & Frameworks", "Guides & Strategies", "Compliance & SOPs", "Digital Marketing"];

export default function Shop() {
  const [selectedCategory, setSelectedCategory] = useState("All Products");
  const [cart, setCart] = useState<typeof digitalProducts>([]);
  const [selectedProduct, setSelectedProduct] = useState<typeof digitalProducts[0] | null>(null);

  const filteredProducts = selectedCategory === "All Products" 
    ? digitalProducts 
    : digitalProducts.filter(product => product.category === selectedCategory);

  const addToCart = (product: typeof digitalProducts[0]) => {
    if (!cart.find(item => item.id === product.id)) {
      setCart([...cart, product]);
    }
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const cartTotal = cart.reduce((total, item) => total + item.price, 0);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-stone-900 mb-6">
              Digital Products & Tools
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 font-light leading-relaxed mb-12">
              Professional templates, frameworks, and guides to accelerate your business success
            </p>
            <div className="bg-yellow-600/10 border border-yellow-600/20 rounded-2xl p-6 max-w-2xl mx-auto">
              <p className="text-stone-700 font-medium">
                ✨ <strong>Launch Special:</strong> All products 30% off for the first 100 customers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-yellow-600 text-white shadow-lg'
                    : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 group relative"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {product.bestseller && (
                  <div className="absolute top-4 left-4 bg-yellow-600 text-white px-3 py-1 text-xs font-bold rounded-full z-10">
                    BESTSELLER
                  </div>
                )}
                {product.featured && (
                  <div className="absolute top-4 left-4 bg-stone-900 text-white px-3 py-1 text-xs font-bold rounded-full z-10">
                    FEATURED
                  </div>
                )}
                
                <div className="aspect-[3/2] overflow-hidden cursor-pointer" onClick={() => setSelectedProduct(product)}>
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                
                <div className="p-6">
                  <div className="mb-3">
                    <span className="text-sm text-yellow-600 font-medium">
                      {product.category}
                    </span>
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-3 cursor-pointer hover:text-yellow-600 transition-colors" onClick={() => setSelectedProduct(product)}>
                    {product.title}
                  </h3>
                  
                  <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  
                  <div className="flex items-center mb-4">
                    <span className="text-2xl font-bold text-stone-900">
                      ${product.price}
                    </span>
                    <span className="text-stone-500 line-through ml-2">
                      ${product.originalPrice}
                    </span>
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full ml-3">
                      SAVE ${product.originalPrice - product.price}
                    </span>
                  </div>
                  
                  <div className="text-xs text-stone-500 mb-4">
                    {product.downloadSize} • {product.fileTypes.join(", ")}
                  </div>
                  
                  <button
                    onClick={() => addToCart(product)}
                    disabled={cart.find(item => item.id === product.id) !== undefined}
                    className={`w-full py-3 rounded-full font-medium transition-all whitespace-nowrap ${
                      cart.find(item => item.id === product.id)
                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white transform hover:scale-105'
                    }`}
                  >
                    {cart.find(item => item.id === product.id) ? 'Added to Cart ✓' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shopping Cart Sidebar */}
      {cart.length > 0 && (
        <div className="fixed right-6 top-32 bg-white rounded-2xl shadow-xl p-6 w-80 z-40 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900">
              Cart ({cart.length})
            </h3>
            <button
              onClick={() => setCart([])}
              className="text-stone-500 hover:text-stone-700 transition-colors"
            >
              Clear All
            </button>
          </div>
          
          <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900 text-sm truncate">
                    {item.title}
                  </p>
                  <p className="text-stone-600 text-sm">
                    ${item.price}
                  </p>
                </div>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="text-stone-500 hover:text-red-600 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}
          </div>
          
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
              <span className="font-medium text-stone-900">Total:</span>
              <span className="font-bold text-xl text-stone-900">${cartTotal}</span>
            </div>
            <button className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-full font-medium transition-all whitespace-nowrap">
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-6 right-6 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-600 hover:text-stone-900 hover:bg-white transition-all z-10"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="aspect-[16/9] overflow-hidden rounded-t-2xl">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="p-8">
              <div className="mb-4">
                <span className="text-sm text-yellow-600 font-medium">
                  {selectedProduct.category}
                </span>
              </div>
              
              <h2 className="font-['Playfair_Display'] text-4xl font-bold text-stone-900 mb-4">
                {selectedProduct.title}
              </h2>
              
              <p className="text-lg text-stone-600 mb-6">
                {selectedProduct.description}
              </p>
              
              <div className="flex items-center mb-6">
                <span className="text-3xl font-bold text-stone-900">
                  ${selectedProduct.price}
                </span>
                <span className="text-stone-500 line-through ml-3 text-xl">
                  ${selectedProduct.originalPrice}
                </span>
                <span className="bg-green-100 text-green-800 text-sm font-bold px-3 py-1 rounded-full ml-4">
                  SAVE ${selectedProduct.originalPrice - selectedProduct.price}
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-4">
                    What's Included
                  </h3>
                  <ul className="space-y-2">
                    {selectedProduct.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-yellow-600 mt-1 text-lg"></i>
                        <span className="text-stone-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-4">
                    Product Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Download Size:</span>
                      <span className="text-stone-900 font-medium">{selectedProduct.downloadSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">File Types:</span>
                      <span className="text-stone-900 font-medium">{selectedProduct.fileTypes.join(", ")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Instant Download:</span>
                      <span className="text-yellow-600 font-medium">✓ Yes</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={() => addToCart(selectedProduct)}
                  disabled={cart.find(item => item.id === selectedProduct.id) !== undefined}
                  className={`flex-1 py-4 rounded-full font-medium transition-all whitespace-nowrap ${
                    cart.find(item => item.id === selectedProduct.id)
                      ? 'bg-green-100 text-green-800 cursor-not-allowed'
                      : 'bg-yellow-600 hover:bg-yellow-700 text-white transform hover:scale-105'
                  }`}
                >
                  {cart.find(item => item.id === selectedProduct.id) ? 'Added to Cart ✓' : 'Add to Cart'}
                </button>
                <button className="px-8 py-4 border-2 border-stone-300 text-stone-600 hover:border-stone-400 hover:text-stone-700 rounded-full font-medium transition-all whitespace-nowrap">
                  Preview Sample
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trust Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-stone-900 mb-12">
            Trusted by 500+ Professionals
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-download-cloud-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Instant Download</h3>
              <p className="text-stone-600">Get immediate access to all files after purchase</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Money-Back Guarantee</h3>
              <p className="text-stone-600">30-day satisfaction guarantee on all products</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Expert Support</h3>
              <p className="text-stone-600">Get help implementing our frameworks</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}